import React, { useContext, useState, useCallback, useMemo, useRef, useEffect } from "react"
import { firestore } from "../firebase/firebase"

const CustomerContext = React.createContext()

export function useCustomer() {
  return useContext(CustomerContext);
}

export function CustomerProvider({ children }) {
  const [customers, setCustomerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // one lazy shared listener so the full customers read happens once and edits stream in as deltas
  const unsubRef = useRef(null);          // active onSnapshot unsubscribe fn
  const startedRef = useRef(false);       // has the listener been attached?
  const customersRef = useRef([]);        // latest snapshot (for promise callers)
  const firstSnapPromise = useRef(null);  // resolves on the first snapshot
  const resolveFirstSnap = useRef(null);

  const startListener = useCallback(() => {
    if (startedRef.current) return firstSnapPromise.current;
    startedRef.current = true;
    setLoading(true);
    firstSnapPromise.current = new Promise((resolve) => {
      resolveFirstSnap.current = resolve;
    });

    unsubRef.current = firestore.collection('customers').onSnapshot(
      (querySnapshot) => {
        const customerResponse = querySnapshot.docs.map((doc) => {
          return {
            ...doc.data(),
            'docId': doc.id,
            'fullStreet': doc.data().streetNumber + ' ' + doc.data().streetName,
          }
        })
        customersRef.current = customerResponse;
        setLoading(false);
        setCustomerData(customerResponse);
        if (resolveFirstSnap.current) {
          resolveFirstSnap.current(customerResponse);
          resolveFirstSnap.current = null;
        }
      },
      (err) => {
        setLoading(false);
        setError(err.message);
        console.log(err.message);
        // allow a future attempt to reattach if the listen failed
        startedRef.current = false;
        unsubRef.current = null;
        if (resolveFirstSnap.current) {
          resolveFirstSnap.current([]);
          resolveFirstSnap.current = null;
        }
      }
    );

    return firstSnapPromise.current;
  }, []);

  // detach the listener when the provider unmounts
  useEffect(() => () => { if (unsubRef.current) unsubRef.current(); }, []);

  // attaches the shared listener on first call and does nothing after that
  const getCustomers = useCallback(() => { startListener(); }, [startListener]);

  // resolves with the customer list once the first snapshot arrives
  const getCustomersPromise = useCallback(async () => {
    await startListener();
    return customersRef.current;
  }, [startListener]);

  const value = useMemo(() => ({
    customers,
    loading,
    error,
    getCustomers,
    getCustomersPromise,
  }), [customers, loading, error, getCustomers, getCustomersPromise]);

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  )
}